import React, { useState, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const LoginPage = () => {
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('main'); // main | magic | magic_sent | magic_verify
  const [email, setEmail] = useState('');

  const verifyMagicLink = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/magic-link/verify`,
        { token }
      );
      const { access_token, user } = response.data;
      login(access_token, user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
      setMode('main');
    } finally {
      setLoading(false);
    }
  }, [login, navigate]);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setMode('magic_verify');
      verifyMagicLink(token);
    }
  }, [searchParams, verifyMagicLink]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/google`,
        { token: credentialResponse.credential }
      );
      const { access_token, user } = response.data;
      login(access_token, user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to login with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/magic-link/request`, { email });
      setMode('magic_sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'magic_verify') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 shadow-lg text-center">
          {loading ? (
            <>
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <p className="text-foreground">Verifying your link...</p>
            </>
          ) : error ? (
            <>
              <p className="text-destructive mb-4">{error}</p>
              <button onClick={() => { setMode('main'); setError(null); }}
                className="text-primary hover:underline text-sm">
                Back to login
              </button>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  if (mode === 'magic_sent') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 shadow-lg text-center">
          <CheckCircle className="w-16 h-16 text-green-700 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Check your email</h2>
          <p className="text-muted-foreground mb-2">
            We sent a sign-in link to
          </p>
          <p className="text-foreground font-medium mb-4">{email}</p>
          <p className="text-sm text-muted-foreground mb-6">
            The link expires in 15 minutes.
          </p>
          <button onClick={() => setMode('main')}
            className="text-primary hover:underline text-sm flex items-center gap-1 mx-auto">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-card border border-border rounded-lg p-8 shadow-lg">
            {/* Logo */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h1 className="text-2xl font-display font-bold text-foreground mb-2">
                Entity Trend Radar
              </h1>
              <p className="text-sm text-muted-foreground">
                Professional Analytics for Gambling Industry
              </p>
            </div>

            <div className="space-y-4">
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                  <p className="text-sm text-destructive text-center">{error}</p>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
              ) : mode === 'main' ? (
                <>
                  {/* Google login */}
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError('Google authentication failed')}
                      theme="filled_black"
                      size="large"
                      text="signin_with"
                      shape="rectangular"
                    />
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground">or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Magic link */}
                  <button
                    onClick={() => setMode('magic')}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-muted hover:bg-muted/80 text-foreground rounded-lg text-sm font-medium transition-colors border border-border"
                  >
                    <Mail className="w-4 h-4" />
                    Sign in with Email Link
                  </button>
                </>
              ) : mode === 'magic' ? (
                <form onSubmit={handleMagicLink} className="space-y-3">
                  <button
                    type="button"
                    onClick={() => { setMode('main'); setError(null); }}
                    className="text-muted-foreground hover:text-foreground text-sm flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-sm font-medium transition-colors"
                  >
                    Send Sign-in Link
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    You must have an existing account. Sign in with Google first to create one.
                  </p>
                </form>
              ) : null}

              <div className="pt-4 text-center">
                <p className="text-xs text-muted-foreground">
                  By signing in, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};
